// Pure presentation helper for a device's self-reported channel labels
// (`deviceChannels` — Maretron RIM100/SIM100 PGN 130818 today).
//
// The boat already normalized the wire payload: channel numbers are 1-based,
// device-level sentinels are dropped, the list is sorted and capped. Neither the
// cloud nor this app re-derives channel numbering — this module only decides
// whether the Channels section renders at all and in what order, which is the
// piece worth a test. Defensive parsing here is about a malformed/legacy row
// surviving a schema change, not about re-deriving anything.

import type { VesselDevice } from './useBoatDevices';

export interface DeviceChannelRow {
  channel: number;
  label: string;
}

/**
 * Rows for the device-detail Channels panel, sorted by channel number.
 *
 * Returns `[]` when the device reports no channels — the caller renders nothing
 * in that case (a GPS or depth sounder has no concept of channels, so a "no
 * channels" card would be noise on nearly every device).
 *
 * Entries that are not a finite channel number plus a non-empty string label are
 * dropped rather than rendered as blanks.
 */
export function deviceChannelRows(device: VesselDevice): DeviceChannelRow[] {
  const raw: unknown = device.deviceChannels;
  if (!Array.isArray(raw)) return [];
  const rows: DeviceChannelRow[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const { channel, label, displayLabel } = entry as {
      channel?: unknown;
      label?: unknown;
      displayLabel?: unknown;
    };
    if (typeof channel !== 'number' || !Number.isFinite(channel)) continue;
    if (typeof label !== 'string' || label === '') continue;
    rows.push({
      channel,
      label: typeof displayLabel === 'string' && displayLabel.trim() !== '' ? displayLabel : label,
    });
  }
  return rows.sort((a, b) => a.channel - b.channel);
}
